/**
 * Settings — routed, grouped, Claude-style (redesign 2026-08-12, see
 * docs/SETTINGS-UX-REVIEW-2026-08.md).
 *
 * One mount mode: the `/settings/:section` route renders this full-screen
 * takeover; every section is a URL, so refresh, back, deep links, and OAuth
 * returns all land on the right panel.
 *
 * Deep-link contract (preserved): `window.openSettings('connections',
 * { connector })` and the `settings:navigate` CustomEvent both resolve here —
 * the connector key travels as `?connector=` and the Connections row scrolls
 * into view, expands, and flashes.
 *
 * Data workbenches (Classification Hub, Procurement, Departments, ZT
 * mapping) are app routes now, not settings — `LEGACY_SECTION_ROUTES` keeps
 * their old settings URLs redirecting.
 */

import React, { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  X,
  Settings as SettingsIcon,
  Brain,
  ShieldCheck,
  Zap,
  Plug,
  DollarSign,
  Crown,
  Key,
  Target,
  Globe,
  Sparkles,
  UserCircle,
  SunMoon,
  PanelLeft,
  Image as ImageIcon,
} from 'lucide-react';
import AdminPanel from '../AdminPanel/AdminPanel';
import BotdoxIntegration from '../BotdoxIntegration/BotdoxIntegration';
import Connections from '../Connections/Connections';
import AICostDashboard from '../AICostDashboard/AICostDashboard';
import SuperAdminDashboard from '../SuperAdminDashboard/SuperAdminDashboard';
import MyApiKeys from '../MyApiKeys/MyApiKeys';
import CompetitiveStrategyAdmin from '../CompetitiveStrategyAdmin/CompetitiveStrategyAdmin';
import AtlasContentAdmin from '../AtlasContentAdmin/AtlasContentAdmin';
import AIPersonalization from '../AIPersonalization/AIPersonalization';
import MemoryDashboardSettings from '../MemoryDashboardSettings/MemoryDashboardSettings';
import ServiceApiKeys from '../ServiceApiKeys/ServiceApiKeys';
import ImageUploadManager from '../ImageUploadManager/ImageUploadManager';
import AccountSection from './AccountSection';
import Appearance from './Appearance';
import Navigation from './Navigation';
import { confirmDiscard } from '../../lib/dirtyGuard';

import './Settings.css';

// Old section ids → new section ids. Keeps every historical deep link
// (`openSettings('my-api-keys')`, bookmarked event dispatches) working.
export const SECTION_ALIASES = {
  'my-account': 'profile',
  'my-api-keys': 'api-keys',
  'service-api-keys': 'api-keys',
  'ai-personalization': 'personalization',
  integrations: 'platform-features',
  admin: 'users',
  // The standalone Calendar page is retired — Google Calendar lives on its
  // Connections row now (same OAuth household, one source of truth).
  'calendar-integration': 'connections',
};

// Former settings sections that are real app destinations now.
export const LEGACY_SECTION_ROUTES = {
  'classification-hub': '/classification-hub',
  'procurement-dashboard': '/procurement',
  'procurement-data': '/procurement',
  departments: '/departments',
  'zt-category-mapping': '/zt-mapping',
  'zt-mapping-eval': '/zt-mapping-eval',
};

// The rail. Groups render with small-caps labels; personal first, power
// below, super-admin last. `gate` is checked BOTH for nav visibility and in
// renderSection — hidden and unrenderable are always the same set.
const SECTION_GROUPS = [
  {
    label: 'Personal',
    items: [
      { id: 'profile', label: 'Profile', icon: UserCircle, description: 'Your sign-in, notifications, and account' },
      { id: 'appearance', label: 'Appearance', icon: SunMoon, description: 'Light or dark theme' },
      { id: 'navigation', label: 'Navigation', icon: PanelLeft, description: 'Which pages appear in your sidebar, and in what order' },
      { id: 'personalization', label: 'AI personalization', icon: Sparkles, description: 'How the AI assistant understands you' },
      { id: 'api-keys', label: 'API keys', icon: Key, description: 'Your provider key and service keys for integrations' },
    ],
  },
  {
    label: 'Platform',
    adminOnly: true,
    items: [
      { id: 'connections', label: 'Connections', icon: Plug, description: 'Every external connection with live-tested health' },
      { id: 'platform-features', label: 'Platform features', icon: Zap, description: 'Botdox feature flags: MCP, discovery, attribution' },
      { id: 'ai-costs', label: 'AI usage & costs', icon: DollarSign, description: 'Spend and usage across AI features' },
      { id: 'memory-dashboard', label: 'Memory observability', icon: Brain, description: 'Sales AI memory system visibility' },
    ],
  },
  {
    label: 'Admin',
    adminOnly: true,
    items: [
      { id: 'users', label: 'Users & access', icon: ShieldCheck, description: 'Members, roles, invitations, feature access' },
      { id: 'brand-assets', label: 'Brand assets', icon: ImageIcon, description: 'Logos, favicons, and site imagery' },
    ],
  },
  {
    label: 'Super admin',
    superAdminOnly: true,
    items: [
      { id: 'super-admin', label: 'Super admin', icon: Crown, description: 'Per-user feature flags and super admin management' },
      { id: 'competitive-strategy', label: 'Competitive strategy', icon: Target, description: 'Per-vendor counter-products, gates, portfolios' },
      { id: 'atlas-content', label: 'Atlas content', icon: Globe, description: 'Educational content for Cybersecurity Atlas' },
    ],
  },
];

export const DEFAULT_SECTION = 'profile';

// A crashing section must say so inside the shell — without this, a
// runtime error in any panel unmounts the entire app to a blank page
// (there is no app-level boundary). Added after a phone-pass report of
// an empty Connections panel; whatever the cause, it must be visible.
class SectionBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sectionId !== this.props.sectionId && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="sx-section-error" role="alert">
          <h2>This section hit an error</h2>
          <p>{String(this.state.error?.message || this.state.error)}</p>
          <button onClick={() => this.setState({ error: null })}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// API keys panel — MyApiKeys (BYO provider key) and ServiceApiKeys
// (keys for external integrations) are one topic; two cards, one section.
function ApiKeysSection() {
  return (
    <div className="sx-stack">
      <MyApiKeys />
      <ServiceApiKeys />
    </div>
  );
}

const SECTION_COMPONENTS = {
  profile: AccountSection,
  appearance: Appearance,
  navigation: Navigation,
  personalization: AIPersonalization,
  'api-keys': ApiKeysSection,
  connections: Connections,
  'platform-features': BotdoxIntegration,
  'ai-costs': AICostDashboard,
  'memory-dashboard': MemoryDashboardSettings,
  users: AdminPanel,
  'brand-assets': ImageUploadManager,
  'super-admin': SuperAdminDashboard,
  'competitive-strategy': CompetitiveStrategyAdmin,
  'atlas-content': AtlasContentAdmin,
};

function Settings({ role = 'cisco_user', isSuperAdmin = false, onExitSettings }) {
  const navigate = useNavigate();
  const { section: rawSection } = useParams();
  const [searchParams] = useSearchParams();

  const visibleGroups = SECTION_GROUPS
    .filter((g) => (g.superAdminOnly ? isSuperAdmin : true))
    .filter((g) => (g.adminOnly ? role === 'admin' : true));
  const visibleItems = visibleGroups.flatMap((g) => g.items);

  const section = SECTION_ALIASES[rawSection] || rawSection || DEFAULT_SECTION;
  const focusConnector = searchParams.get('connector') || null;

  // settings:navigate contract: components may dispatch the event directly
  // (detail: { section, connector }); it resolves to a route change so the
  // URL stays the source of truth.
  useEffect(() => {
    const handleNavigate = (e) => {
      const next = e?.detail?.section;
      if (!next) return;
      const workspace = LEGACY_SECTION_ROUTES[next];
      if (workspace) { navigate(workspace); return; }
      const target = SECTION_ALIASES[next] || next;
      const connector = e?.detail?.connector;
      navigate(`/settings/${target}${connector ? `?connector=${encodeURIComponent(connector)}` : ''}`);
    };
    window.addEventListener('settings:navigate', handleNavigate);
    return () => window.removeEventListener('settings:navigate', handleNavigate);
  }, [navigate]);

  // Leaving Settings (Esc, Back, X) or switching sections checks the dirty
  // registry first — a form with unsaved edits gets a discard confirm
  // instead of silently losing input.
  const exitSettings = async () => {
    if (!(await confirmDiscard())) return;
    onExitSettings?.();
  };

  const goToSection = async (id) => {
    if (id === section) return;
    if (!(await confirmDiscard())) return;
    navigate(`/settings/${id}`);
  };

  // Escape exits Settings — but never while focus is in a form control, so
  // it can't discard mid-typed input (review item #12). Components that
  // handle Escape themselves stopPropagation and are unaffected.
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (e.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      confirmDiscard().then((ok) => { if (ok) onExitSettings?.(); });
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [onExitSettings]);

  // Old workspace sections redirect out to their app routes.
  const workspaceRoute = LEGACY_SECTION_ROUTES[rawSection];
  if (workspaceRoute) return <Navigate to={workspaceRoute} replace />;

  // Unknown or not-permitted section → land on Profile. Nav visibility and
  // renderability are the same check, so there is no crafted-section render.
  const currentItem = visibleItems.find((item) => item.id === section);
  if (!currentItem) return <Navigate to={`/settings/${DEFAULT_SECTION}`} replace />;

  const SectionComponent = SECTION_COMPONENTS[section];
  const sectionProps = section === 'connections' ? { focusConnector } : {};

  return (
    <div className="settings-overlay">
      <header className="sx-header">
        <div className="sx-header-left">
          <button className="sx-back" onClick={exitSettings} title="Back to the app">
            <ArrowLeft size={16} strokeWidth={2} />
            <span>Back</span>
          </button>
          <nav className="sx-crumbs" aria-label="Settings breadcrumb">
            <span className="sx-crumb-root">
              <SettingsIcon size={15} strokeWidth={2} />
              Settings
            </span>
            <span className="sx-crumb-sep" aria-hidden="true">/</span>
            <span className="sx-crumb-current">{currentItem.label}</span>
          </nav>
        </div>
        <div className="sx-header-right">
          <div className="sx-esc-hint" aria-hidden="true">
            <kbd>Esc</kbd>
            <span>to exit</span>
          </div>
          <button className="sx-close" onClick={exitSettings} title="Close settings" aria-label="Close settings">
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      </header>

      <div className="sx-body">
        <nav className="sx-rail" aria-label="Settings sections">
          {visibleGroups.map((group) => (
            <div key={group.label} className="sx-group">
              <div className="sx-group-label">{group.label}</div>
              {group.items.map((item) => {
                const ItemIcon = item.icon;
                const active = item.id === section;
                return (
                  <button
                    key={item.id}
                    className={`sx-nav-item${active ? ' sx-nav-item--active' : ''}`}
                    onClick={() => goToSection(item.id)}
                    aria-current={active ? 'page' : undefined}
                    title={item.description}
                  >
                    <ItemIcon size={17} strokeWidth={2} aria-hidden="true" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <main className="sx-content">
          <div className="settings-section" key={section}>
            <SectionBoundary sectionId={section}>
              {SectionComponent ? (
                <SectionComponent {...sectionProps} />
              ) : (
                // A rail item without a map entry is a wiring bug — say so
                // instead of rendering silent nothing (this exact hole hid
                // the missing `connections` entry for a full release).
                <div className="sx-section-error" role="alert">
                  <h2>This section isn’t wired up</h2>
                  <p>{`No component registered for section "${section}" — SECTION_COMPONENTS in Settings.js is missing an entry.`}</p>
                </div>
              )}
            </SectionBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Settings;
